import React, { useMemo } from 'react';
import { useCaeActionPlanAging } from '../../../hooks/analytics/useCaeReports.ts';
import { formatDate, StatusChip, WidgetCard } from '../controller/shared.tsx';

function agingClasses(daysOverdue: number | null): { row: string; badge: string } {
  if (daysOverdue == null) return { row: '', badge: 'bg-gray-100 text-gray-700' };
  if (daysOverdue > 60) return { row: 'bg-red-50', badge: 'bg-red-100 text-red-700' };
  if (daysOverdue > 30) return { row: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700' };
  return { row: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-700' };
}

/** Report #16 — Action Plan Aging. Overdue action plans, color-coded by days overdue (0-30/31-60/60+). */
export const ActionPlanAgingTable: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeActionPlanAging();
  const rows = useMemo(
    () => [...(data ?? [])].sort((a, b) => (b.days_overdue ?? 0) - (a.days_overdue ?? 0)),
    [data]
  );

  return (
    <WidgetCard
      title="Action Plan Aging"
      description="Overdue action plans — 0-30 days (yellow), 31-60 (orange), 60+ (red)"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load action plan aging.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No overdue action plans."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="analytics-scroll max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500 sticky top-0 bg-white">
              <th className="pb-2">Action</th>
              <th className="pb-2">Issue</th>
              <th className="pb-2">Owner</th>
              <th className="pb-2">Due</th>
              <th className="pb-2">Days Overdue</th>
              <th className="pb-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => {
              const { row: rowClass, badge } = agingClasses(row.days_overdue);
              return (
                <tr key={row.id} className={rowClass}>
                  <td className="py-2 pr-2 text-gray-900 max-w-xs truncate">{row.action_title}</td>
                  <td className="py-2 pr-2 text-gray-600 max-w-xs truncate">{row.issue_title}</td>
                  <td className="py-2 pr-2 text-gray-600">{row.owner_name ?? '—'}</td>
                  <td className="py-2 pr-2 text-gray-600">{formatDate(row.due_date)}</td>
                  <td className="py-2 pr-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge}`}>
                      {row.days_overdue ?? '—'} days
                    </span>
                  </td>
                  <td className="py-2">
                    <StatusChip value={row.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
};

export default ActionPlanAgingTable;
